import { DotsThree } from '@phosphor-icons/react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

export interface ChatMenuItem {
  key: string
  labelKey: string
  onSelect: () => void
  danger?: boolean
  disabled?: boolean
}

interface ChatMenuProps {
  items: ChatMenuItem[]
  labelKey?: string
  label?: string
  className?: string
}

/**
 * Keyboard-accessible overflow menu used by chat rows, chat headers and
 * message bubbles. Native `<details>` keeps Enter/Space/focus behavior without
 * a portal; Escape and outside clicks close it and restore the trigger.
 */
export function ChatMenu({
  items,
  labelKey = 'chat.actions.options',
  label,
  className = '',
}: ChatMenuProps) {
  const { t } = useTranslation()
  const detailsRef = useRef<HTMLDetailsElement>(null)
  const summaryRef = useRef<HTMLElement>(null)

  useEffect(() => {
    function close() {
      if (detailsRef.current?.open) detailsRef.current.open = false
    }

    function onPointerDown(event: MouseEvent) {
      if (!detailsRef.current?.open) return
      if (!detailsRef.current.contains(event.target as Node)) close()
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || !detailsRef.current?.open) return
      close()
      summaryRef.current?.focus()
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  if (items.length === 0) return null

  return (
    <details ref={detailsRef} className={`chat-menu ${className}`}>
      <summary
        ref={summaryRef}
        className="chat-menu__trigger"
        aria-label={label ?? t(labelKey)}
        aria-haspopup="menu"
      >
        <DotsThree size={22} weight="bold" aria-hidden="true" />
      </summary>
      <div className="chat-menu__panel" role="menu">
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            className={`chat-menu__item ${item.danger ? 'chat-menu__item--danger' : ''}`}
            disabled={item.disabled}
            onClick={() => {
              if (detailsRef.current) detailsRef.current.open = false
              item.onSelect()
            }}
          >
            {t(item.labelKey)}
          </button>
        ))}
      </div>
    </details>
  )
}
